import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  X, 
  Loader2, 
  Mail, 
  Lock, 
  User, 
  Building2,
  Globe,
  Shield,
  ArrowLeft,
  KeyRound
} from "lucide-react";

const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const { login, register, forgotPassword, resetPassword } = useAuth();
  const [mode, setMode] = useState("login"); // login, register, forgot, reset
  const [isLoading, setIsLoading] = useState(false);
  const [resetToken, setResetToken] = useState("");
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    organization: "",
    newPassword: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (mode === "login") {
        await login(formData.email, formData.password);
        toast.success("Welcome back!");
        onSuccess?.();
        onClose();
      } else if (mode === "register") {
        if (formData.password.length < 8) {
          toast.error("Password must be at least 8 characters");
          setIsLoading(false);
          return;
        }
        await register(
          formData.email,
          formData.password,
          formData.name,
          formData.organization
        );
        toast.success("Account created successfully!");
        onSuccess?.();
        onClose();
      } else if (mode === "forgot") {
        const result = await forgotPassword(formData.email);
        if (result.reset_token) {
          setResetToken(result.reset_token);
          setMode("reset");
          toast.success("Reset token generated. Enter your new password.");
        } else {
          toast.info("If an account exists, a reset link has been sent.");
        }
      } else if (mode === "reset") {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error("Passwords do not match");
          setIsLoading(false);
          return;
        }
        if (formData.newPassword.length < 8) {
          toast.error("Password must be at least 8 characters");
          setIsLoading(false);
          return;
        }
        await resetPassword(resetToken, formData.newPassword);
        toast.success("Password reset successfully! Please login.");
        setMode("login");
        setFormData({ ...formData, password: "", newPassword: "", confirmPassword: "" });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Operation failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setFormData({ email: "", password: "", name: "", organization: "", newPassword: "", confirmPassword: "" });
    setResetToken("");
  };

  if (!isOpen) return null;

  const getTitle = () => {
    switch (mode) {
      case "login": return "Welcome Back";
      case "register": return "Create Account";
      case "forgot": return "Reset Password";
      case "reset": return "Set New Password";
      default: return "Authentication";
    }
  };

  const getSubtitle = () => {
    switch (mode) {
      case "login": return "Sign in to access meteorological translation tools";
      case "register": return "Register for secure access to ECCC translation services";
      case "forgot": return "Enter your email to receive a password reset link";
      case "reset": return "Enter your new password below";
      default: return "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" data-testid="auth-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primary-hover px-6 py-8 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="auth-close-btn"
          >
            <X className="w-5 h-5" />
          </button>
          
          {(mode === "forgot" || mode === "reset") && (
            <button
              onClick={() => switchMode("login")}
              className="absolute top-4 left-4 text-white/70 hover:text-white transition-colors flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          )}
          
          <div className="flex items-center gap-3 mb-3">
            <Globe className="w-8 h-8" />
            <span className="font-heading text-xl font-bold">MeteoTranslate</span>
          </div>
          
          <h2 className="text-2xl font-bold mb-1" data-testid="auth-title">
            {getTitle()}
          </h2>
          <p className="text-white/80 text-sm">
            {getSubtitle()}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {mode === "register" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium text-foreground">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="John Smith"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="pl-10"
                    data-testid="auth-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="organization" className="text-sm font-medium text-foreground">
                  Organization <span className="text-muted-foreground">(Optional)</span>
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="organization"
                    name="organization"
                    type="text"
                    placeholder="Environment Canada"
                    value={formData.organization}
                    onChange={handleChange}
                    className="pl-10"
                    data-testid="auth-org-input"
                  />
                </div>
              </div>
            </>
          )}

          {(mode === "login" || mode === "register" || mode === "forgot") && (
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="name@government.gc.ca"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10"
                  data-testid="auth-email-input"
                />
              </div>
            </div>
          )}

          {(mode === "login" || mode === "register") && (
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder={mode === "login" ? "••••••••" : "Min. 8 characters"}
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={mode === "login" ? 1 : 8}
                  className="pl-10"
                  data-testid="auth-password-input"
                />
              </div>
            </div>
          )}

          {mode === "reset" && (
            <>
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-foreground">
                  New Password
                </Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    placeholder="Min. 8 characters"
                    value={formData.newPassword}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="pl-10"
                    data-testid="auth-new-password-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-foreground">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={8}
                    className="pl-10"
                    data-testid="auth-confirm-password-input"
                  />
                </div>
              </div>
            </>
          )}

          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary-hover text-white h-11"
            disabled={isLoading}
            data-testid="auth-submit-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                {mode === "login" ? "Signing in..." : 
                 mode === "register" ? "Creating account..." :
                 mode === "forgot" ? "Sending..." : "Resetting..."}
              </>
            ) : (
              <>
                <Shield className="w-4 h-4 mr-2" />
                {mode === "login" ? "Sign In" : 
                 mode === "register" ? "Create Account" :
                 mode === "forgot" ? "Send Reset Link" : "Reset Password"}
              </>
            )}
          </Button>

          <div className="text-center pt-2 space-y-2">
            {mode === "login" && (
              <>
                <button
                  type="button"
                  onClick={() => switchMode("forgot")}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors block w-full"
                  data-testid="auth-forgot-btn"
                >
                  Forgot your password?
                </button>
                <button
                  type="button"
                  onClick={() => switchMode("register")}
                  className="text-sm text-primary hover:underline"
                  data-testid="auth-toggle-btn"
                >
                  Don't have an account? Register
                </button>
              </>
            )}
            
            {mode === "register" && (
              <button
                type="button"
                onClick={() => switchMode("login")}
                className="text-sm text-primary hover:underline"
                data-testid="auth-toggle-btn"
              >
                Already have an account? Sign in
              </button>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center bg-muted rounded-lg p-3">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>Secure government-grade authentication</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
